import { Component, DestroyRef, signal, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of, tap } from 'rxjs';
import { ErrorHandler } from '@core/utils/error-handler';
import { LayoutService } from '@layout/service/app.layout.service';
import { AuthService } from '@core/services/auth/auth.service';
import { UserLogin } from '@core/services/auth/models/user-login';
import { ToastService } from '@layout/service/toast.service';
import { Title } from '@angular/platform-browser';
import { User } from '@core/models';

interface LoginForm {
  email: FormControl<string>;
  password: FormControl<string>;
}

interface Message {
  severity: 'success' | 'info' | 'warn' | 'error';
  content: string;
}

@Component({
  selector: 'cwa-login',
  templateUrl: 'login.component.html',
  standalone: false,
})
export class LoginComponent {
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private layoutService = inject(LayoutService);
  private authService = inject(AuthService);
  private toast = inject(ToastService);
  private titleService = inject(Title);

  loading = false;
  messages = signal<Message[]>([]);
  visible = false;
  userId = '';

  loginForm: FormGroup<LoginForm> = this.createForm();
  controls = {
    email: this.loginForm.get('email'),
    password: this.loginForm.get('password'),
  };

  constructor() {
    this.titleService.setTitle('Auth - Login | Cogwheel Analytics Tools');
  }

  get dark(): boolean {
    return this.layoutService.config.colorScheme !== 'light';
  }

  login(): void {
    if (this.loginForm.invalid) return;

    const { email, password } = this.loginForm.value;

    if (email && password) {
      const credential: UserLogin = { email, password };

      this.loading = true;
      this.authService
        .login(credential)
        .pipe(
          tap((user: User) => {
            if (user && user?.is_accepted_tnc === true) {
              // User has accepted T&C, navigate to home
              // Push login event to dataLayer
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({
                event: 'login',
                username: (user?.firstName || '') + (user?.lastName || '') || 'unknown',
                enterprise: user?.enterprises || [],
              });

              this.router.navigateByUrl('/');
            } else if (user && user?.is_accepted_tnc === false) {
              // User has not accepted T&C, show dialog
              this.userId = user.id;
              this.showDialog();
            }
          }),
          catchError((error) => {
            this.loading = false;
            // Commented for future reference
            // this.showError(error.error?.detail);
            const errorMessage = ErrorHandler.extractErrorMessage(error);
            this.showError(errorMessage);
            return of(null);
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    }
  }

  private showError(content: string): void {
    this.messages.set([
      {
        severity: 'error',
        content,
      },
    ]);
  }

  showDialog() {
    this.visible = true;
  }

  doAgree() {
    this.authService
      .updateTnCAgreement(this.userId, { isAcceptedTnc: true })
      .pipe(
        tap(() => {
          this.visible = false;
          this.loading = false;
          this.toast.showSuccess('You agreed with our T&C and Policy', '');
          // Navigate to home page after successful TnC agreement
          setTimeout(() => {
            this.router.navigateByUrl('/');
          }, 0);
        }),
        catchError((error) => {
          console.error('Error updating T&C agreement:', error);
          this.loading = false;
          this.visible = false;
          this.toast.showError('Failed to update T&C agreement', '');
          return of(null);
        })
      )
      .subscribe();
  }

  cancelAgree() {
    this.visible = false;
    this.loading = false;
    // Show toast before clearing tokens
    this.toast.showInfo('You have to agree with our T&C and Policy', '');
    // Clear tokens when user disagrees with TnC (after a small delay to ensure toast renders)
    setTimeout(() => {
      this.authService.logout();
    }, 100);
  }

  private createForm(): FormGroup<LoginForm> {
    return this.fb.group(<LoginForm>{
      email: this.fb.control('', [Validators.email, Validators.required]),
      password: this.fb.control('', [Validators.required]),
    });
  }
}
